import React, { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, Power, Shield } from "lucide-react";
import { usersService } from "../services/api";
import { toEnglishNumbers, toPersianNumbers } from "../utils/numbers";
import { Modal } from "../components/Modal";
import { SearchableSelect } from "../components/SearchableSelect";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  national_code: string;
  mobile: string;
  bale_id?: string;
  role: string;
  permissions?: string[];
  is_active?: boolean;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);

  // Modal State
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info" | "confirm";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    national_code: "",
    mobile: "",
    bale_id: "",
    role: "user",
  });
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersService.getUsers();
      const usersArray = Array.isArray(response)
        ? response
        : response.data || response.users || [];

      setUsers(usersArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    setModalState({
      isOpen: true,
      title: "تایید حذف کاربر",
      message: `آیا از حذف ${user.first_name} ${user.last_name} اطمینان دارید؟`,
      type: "confirm",
      onConfirm: async () => {
        try {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          await usersService.deleteUser(user.id);
          setUsers(users.filter((u) => u.id !== user.id));
          setTimeout(() => {
            setModalState({
              isOpen: true,
              title: "موفق",
              message: "کاربر با موفقیت حذف شد.",
              type: "success",
            });
          }, 300);
        } catch (error: any) {
          console.error("Error deleting user:", error);
          setTimeout(() => {
            setModalState({
              isOpen: true,
              title: "خطا",
              message: error.response?.data?.message || "خطا در حذف کاربر",
              type: "error",
            });
          }, 300);
        }
      },
    });
  };

  const handleToggleActivation = async (user: User) => {
    const newStatus = !user.is_active;
    const actionText = newStatus ? "فعال" : "غیرفعال";

    setModalState({
      isOpen: true,
      title: `تایید ${actionText} سازی`,
      message: `آیا از ${actionText} کردن ${user.first_name} ${user.last_name} اطمینان دارید؟`,
      type: "confirm",
      onConfirm: async () => {
        try {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          await usersService.toggleUserActivation(user.id, newStatus);
          setUsers(
            users.map((u) =>
              u.id === user.id ? { ...u, is_active: newStatus } : u,
            ),
          );
          setTimeout(() => {
            setModalState({
              isOpen: true,
              title: "موفق",
              message: `کاربر با موفقیت ${actionText} شد.`,
              type: "success",
            });
          }, 300);
        } catch (error: any) {
          console.error("Error toggling user activation:", error);
          setTimeout(() => {
            setModalState({
              isOpen: true,
              title: "خطا",
              message:
                error.response?.data?.message ||
                `خطا در ${actionText} کردن کاربر`,
              type: "error",
            });
          }, 300);
        }
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      formData.national_code.length !== 10 ||
      !/^\d+$/.test(formData.national_code)
    ) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "کد ملی باید دقیقاً ۱۰ رقم باشد.",
        type: "error",
      });
      return;
    }

    if (formData.mobile.length !== 11 || !/^\d+$/.test(formData.mobile)) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "شماره موبایل باید دقیقاً ۱۱ رقم باشد.",
        type: "error",
      });
      return;
    }

    try {
      if (editingUserId) {
        await usersService.updateUser(editingUserId, formData);
        fetchUsers();
        setIsEditUserModalOpen(false);
        setEditingUserId(null);
        setFormData({
          first_name: "",
          last_name: "",
          national_code: "",
          mobile: "",
          bale_id: "",
          role: "user",
        });
        setModalState({
          isOpen: true,
          title: "موفق",
          message: "کاربر با موفقیت ویرایش شد.",
          type: "success",
        });
      } else {
        await usersService.createUser(formData);
        fetchUsers();
        setIsAddModalOpen(false);
        setFormData({
          first_name: "",
          last_name: "",
          national_code: "",
          mobile: "",
          bale_id: "",
          role: "user",
        });
        setModalState({
          isOpen: true,
          title: "موفق",
          message: "کاربر با موفقیت اضافه شد.",
          type: "success",
        });
      }
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error?.response?.data?.message || "خطا در سیستم",
        type: "error",
      });
    }
  };

  const handleAddUser = handleSubmit;

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    try {
      await usersService.updateUserPermissions(
        selectedUser.id,
        userPermissions,
      );
      setUsers(
        users.map((u) =>
          u.id === selectedUser.id ? { ...u, permissions: userPermissions } : u,
        ),
      );
      setIsPermissionsModalOpen(false);
      setSelectedUser(null);
      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message: "دسترسی‌ها با موفقیت ذخیره شدند.",
        type: "success",
      });
    } catch (error) {
      console.error("Error saving permissions:", error);
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "خطا در ذخیره دسترسی‌ها",
        type: "error",
      });
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.first_name.includes(search) ||
      user.last_name.includes(search) ||
      user.national_code.includes(search),
  );

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            مدیریت کاربران
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            مشاهده و مدیریت کاربران سامانه
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center justify-center gap-2 bg-persian-600 hover:bg-persian-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-lg shadow-persian-900/10 dark:shadow-none"
        >
          <Plus className="w-5 h-5" />
          <span>کاربر جدید</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="جستجو بر اساس نام، نام خانوادگی یا کد ملی..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-persian-500 focus:ring-4 focus:ring-persian-50/50 dark:focus:ring-persian-500/20 transition-all dark:text-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-right border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-slate-50/50 dark:bg-slate-900/20 text-slate-500 dark:text-slate-400 text-sm">
              <th className="font-medium p-4 border-b border-slate-200 dark:border-slate-700">
                نام و نام خانوادگی
              </th>
              <th className="font-medium p-4 border-b border-slate-200 dark:border-slate-700">
                کد ملی
              </th>
              <th className="font-medium p-4 border-b border-slate-200 dark:border-slate-700">
                شماره موبایل
              </th>
              <th className="font-medium p-4 border-b border-slate-200 dark:border-slate-700">
                نقش
              </th>
              <th className="font-medium p-4 border-b border-slate-200 dark:border-slate-700 w-24">
                عملیات
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="p-8 text-center text-slate-500 dark:text-slate-400"
                >
                  در حال بارگذاری...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="p-8 text-center text-slate-500 dark:text-slate-400"
                >
                  کاربری یافت نشد.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-persian-600 dark:text-persian-400 font-medium">
                        {user.first_name?.[0]}
                      </div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {user.first_name} {user.last_name}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-slate-600 dark:text-slate-300">
                    {toPersianNumbers(user.national_code)}
                  </td>
                  <td className="p-4 text-slate-600 dark:text-slate-300">
                    {toPersianNumbers(user.mobile)}
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.role === "admin"
                          ? "bg-persian-50 text-persian-700 dark:bg-persian-900/30 dark:text-persian-400"
                          : user.role === "investor"
                            ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                      }`}
                    >
                      {user.role === "admin"
                        ? "مدیر سیستم"
                        : user.role === "investor"
                          ? "سرمایه گذار"
                          : "کاربر عادی"}
                    </span>
                    {!user.is_active && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        غیرفعال
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-start gap-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setUserPermissions(user.permissions || []);
                          setIsPermissionsModalOpen(true);
                        }}
                        className="p-1.5 text-slate-400 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
                        title="دسترسی‌ها"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-slate-400 hover:text-persian-600 dark:hover:text-persian-400 transition-colors rounded-lg hover:bg-persian-50 dark:hover:bg-persian-900/20">
                        <Edit2 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleToggleActivation(user)}
                        className={`p-1.5 transition-colors rounded-lg ${user.is_active ? "text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20" : "text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"}`}
                        title={user.is_active ? "غیرفعال کردن" : "فعال کردن"}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {isEditUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                ویرایش مشخصات کاربر
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    نام
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    نام خانوادگی
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  کد ملی
                </label>
                <input
                  type="text"
                  required
                  maxLength={10}
                  value={toPersianNumbers(formData.national_code)}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value);
                    if (/^\d*$/.test(val))
                      setFormData({ ...formData, national_code: val });
                  }}
                  className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all text-left"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  شماره موبایل
                </label>
                <input
                  type="text"
                  required
                  maxLength={11}
                  value={toPersianNumbers(formData.mobile)}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value);
                    if (/^\d*$/.test(val))
                      setFormData({ ...formData, mobile: val });
                  }}
                  className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all text-left"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  آیدی بله
                </label>
                <input
                  type="text"
                  value={formData.bale_id}
                  onChange={(e) =>
                    setFormData({ ...formData, bale_id: e.target.value })
                  }
                  className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all text-left"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  نقش کاربر
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full h-11 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all"
                >
                  <option value="user">کاربر عادی</option>
                  <option value="investor">سرمایه گذار</option>
                  <option value="admin">مدیر سیستم</option>
                </select>
              </div>
              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-11 rounded-xl font-medium transition-colors"
                >
                  ذخیره تغییرات
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditUserModalOpen(false);
                    setEditingUserId(null);
                  }}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                تعریف کاربر جدید
              </h2>
            </div>

            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    نام
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={20}
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    نام خانوادگی
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={30}
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  کد ملی
                </label>
                <input
                  type="text"
                  required
                  maxLength={10}
                  minLength={10}
                  pattern="[0-9۰-۹]{10}"
                  value={toPersianNumbers(formData.national_code)}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value).replace(
                      /\D/g,
                      "",
                    );
                    setFormData({ ...formData, national_code: val });
                  }}
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  شماره موبایل
                </label>
                <input
                  type="text"
                  required
                  maxLength={11}
                  minLength={11}
                  pattern="[0-9۰-۹]{11}"
                  value={toPersianNumbers(formData.mobile)}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value).replace(
                      /\D/g,
                      "",
                    );
                    setFormData({ ...formData, mobile: val });
                  }}
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  آیدی بله
                </label>
                <input
                  type="text"
                  value={formData.bale_id}
                  onChange={(e) =>
                    setFormData({ ...formData, bale_id: e.target.value })
                  }
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                  dir="ltr"
                  placeholder="آیدی عددی"
                />
              </div>

              <div className="space-y-2">
                <SearchableSelect
                  label="نقش"
                  options={[
                    { value: "user", label: "کاربر عادی" },
                    { value: "admin", label: "مدیر سیستم" },
                    { value: "investor", label: "سرمایه گذار" },
                  ]}
                  value={formData.role}
                  onChange={(val) => setFormData({ ...formData, role: val })}
                  required
                />
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-10 rounded-xl font-medium transition-colors"
                >
                  ثبت کاربر
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setFormData({
                      first_name: "",
                      last_name: "",
                      national_code: "",
                      mobile: "",
                      bale_id: "",
                      role: "user",
                    });
                  }}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-10 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {isPermissionsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                مدیریت دسترسی‌ها: {selectedUser.first_name}{" "}
                {selectedUser.last_name}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                {[
                  { id: "dashboard", label: "داشبورد (صفحه اصلی)" },
                  { id: "investments", label: "سرمایه‌گذاری" },
                  { id: "weekly-penalties", label: "جریمه‌های هفتگی" },
                  { id: "balance", label: "موجودی صندوق" },
                  { id: "installments", label: "تایید اقساط پرداختی" },
                  { id: "loans", label: "ثبت نام اولیه وام" },
                  { id: "loan-reservations", label: "تایید ثبت اولیه" },
                  { id: "loan-fee-receipt", label: "تایید کارمزد وام" },
                  { id: "loan-payments", label: "پرداخت وام‌ها" },
                  { id: "loan-history", label: "تاریخچه وام‌ها" },
                  { id: "expenses", label: "هزینه‌ها" },
                  { id: "users", label: "مدیریت کاربران" },
                ].map((page) => (
                  <label
                    key={page.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={userPermissions.includes(page.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUserPermissions([...userPermissions, page.id]);
                        } else {
                          setUserPermissions(
                            userPermissions.filter((p) => p !== page.id),
                          );
                        }
                      }}
                      className="w-5 h-5 rounded border-slate-300 text-persian-600 focus:ring-persian-500"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {page.label}
                    </span>
                  </label>
                ))}
              </div>
              <div className="pt-4 flex gap-3">
                <button
                  onClick={handleSavePermissions}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white h-10 rounded-xl font-medium transition-colors"
                >
                  ذخیره دسترسی‌ها
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsPermissionsModalOpen(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-10 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
      />
    </div>
  );
}
